import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Lock, Clock, Calendar, Users, MessageCircle, 
  Radio, Tv, ChevronRight, Ticket, AlertCircle, CheckCircle,
  Volume2, Settings, Maximize, Share2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if user is admin — use main auth context (works with /login flow)
const isAdminUserFallback = () => {
  return !!sessionStorage.getItem('admin_auth');
};

// Twitch Chat Embed
const TwitchChat = ({ channel }) => {
  if (!channel) return null;
  
  return (
    <iframe
      src={`https://www.twitch.tv/embed/${channel}/chat?parent=${window.location.hostname}&darkpopout`}
      className="w-full h-full"
      frameBorder="0"
      title="Twitch Chat"
    />
  );
};

export default function PPVWatch() {
  const { user, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessTier, setAccessTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(true);
  const [accessEmail, setAccessEmail] = useState('');
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [accessError, setAccessError] = useState('');
  
  // Admin bypass — check BOTH the main auth context (from /login) and the
  // legacy sessionStorage flag (from /admin/login) so either flow unlocks.
  const adminAccess = (typeof isAdmin === 'function' && isAdmin()) || isAdminUserFallback();

  useEffect(() => {
    fetchSettings();
    
    // Admin users have full access
    if (adminAccess) {
      setHasAccess(true);
      setAccessTier('vip');
      return;
    }
    
    // Check if coming from successful purchase
    const sessionId = searchParams.get('session_id');
    const tier = searchParams.get('tier');
    if (sessionId && tier) {
      setHasAccess(true);
      setAccessTier(tier);
    }
  }, [searchParams, adminAccess]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ppv/settings`);
      const data = await response.json();
      setSettings(data);
      
      // Get current/most recent event
      if (data.upcomingEvents?.length > 0) {
        // Find live event first, otherwise use first upcoming
        const liveEvent = data.upcomingEvents.find(e => e.isLive);
        setCurrentEvent(liveEvent || data.upcomingEvents[0]);
      }
    } catch (error) {
      console.error('Error fetching PPV settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAccessByEmail = async () => {
    if (!accessEmail || !accessEmail.includes('@')) {
      setAccessError('Please enter a valid email address');
      return;
    }

    setCheckingAccess(true);
    setAccessError('');

    try {
      // Check purchases by email
      const response = await fetch(`${API_URL}/api/ppv/access-by-email?email=${encodeURIComponent(accessEmail)}&eventId=${encodeURIComponent(currentEvent?.title || '')}`);
      const data = await response.json();
      
      if (data.hasAccess) {
        setHasAccess(true);
        setAccessTier(data.tier);
      } else {
        setAccessError('No purchase found for this email. Please check your email or purchase a ticket.');
      }
    } catch (error) {
      // For now, allow access if API fails (graceful fallback)
      console.error('Error checking access:', error);
      setAccessError('Unable to verify access. Please try again.');
    } finally {
      setCheckingAccess(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading stream...</p>
        </div>
      </div>
    );
  }

  if (!currentEvent) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <Tv className="w-20 h-20 text-zinc-600 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">No Active Event</h1>
          <p className="text-zinc-400 mb-8">
            There are no live or upcoming events at the moment. Check back soon!
          </p>
          <Link
            to="/ppv"
            className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-500 text-black font-bold rounded-xl hover:bg-yellow-400 transition-colors"
          >
            View Upcoming Events
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {currentEvent.isLive ? (
              <span className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/50 rounded-full">
                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-red-400 font-bold text-sm">LIVE NOW</span>
              </span>
            ) : (
              <span className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/50 rounded-full">
                <Radio className="w-4 h-4 text-yellow-500" />
                <span className="text-yellow-400 font-bold text-sm">UPCOMING</span>
              </span>
            )}
            <div>
              <h1 className="text-lg font-bold text-white">{currentEvent.title}</h1>
              <p className="text-sm text-zinc-400">{currentEvent.subtitle}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {hasAccess && (
              <span className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                <CheckCircle className="w-4 h-4" />
                Access Granted
              </span>
            )}
            <Link
              to="/ppv"
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm font-medium transition-colors"
            >
              Back to Events
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {hasAccess ? (
          /* ========== HAS ACCESS - SHOW STREAM ========== */
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Video Player */}
            <div className={`${showChat ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
              <div className="bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800">
                {/* Stream */}
                <div className="aspect-video bg-black relative">
                  {currentEvent.isLive && currentEvent.twitchChannel ? (
                    <iframe
                      src={`https://player.twitch.tv/?channel=${currentEvent.twitchChannel}&parent=${window.location.hostname}`}
                      className="w-full h-full"
                      frameBorder="0"
                      allowFullScreen
                      title="Live Stream"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <motion.div
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <Tv className="w-20 h-20 text-zinc-600 mx-auto mb-4" />
                        </motion.div>
                        <h3 className="text-xl font-bold text-white mb-2">Stream Starting Soon</h3>
                        <p className="text-zinc-400 mb-4">
                          {new Date(currentEvent.date).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            month: 'long', 
                            day: 'numeric' 
                          })} at {currentEvent.time}
                        </p>
                        <div className="flex items-center justify-center gap-4 text-sm text-zinc-500">
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {currentEvent.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Controls bar */}
                <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-zinc-400">
                      Your access: <span className="text-yellow-400 font-medium capitalize">{accessTier || 'Standard'}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowChat(!showChat)}
                      className={`p-2 rounded-lg transition-colors ${showChat ? 'bg-yellow-500/20 text-yellow-400' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
                    >
                      <MessageCircle className="w-5 h-5" />
                    </button>
                    <button className="p-2 bg-zinc-800 rounded-lg text-zinc-400 hover:text-white transition-colors">
                      <Share2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Event Info */}
              <div className="mt-6 bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <h2 className="text-xl font-bold text-white mb-4">About This Event</h2>
                <p className="text-zinc-400 leading-relaxed">{currentEvent.description}</p>
                
                {currentEvent.highlights?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-bold text-zinc-300 mb-3">Event Highlights</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {currentEvent.highlights.map((highlight, index) => (
                        <div key={index} className="flex items-center gap-3 text-sm text-zinc-400">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {typeof highlight === 'string' ? highlight : highlight.title}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Chat Sidebar */}
            {showChat && (
              <div className="lg:col-span-1">
                <div className="bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800 h-[600px] flex flex-col">
                  <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-yellow-500" />
                      Live Chat
                    </h3>
                    {accessTier === 'vip' && (
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded">VIP</span>
                    )}
                  </div>
                  <div className="flex-1">
                    {currentEvent.twitchChannel ? (
                      <TwitchChat channel={currentEvent.twitchChannel} />
                    ) : (
                      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
                        Chat will be available when the stream starts
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ========== NO ACCESS - SHOW UNLOCK SCREEN ========== */
          <div className="max-w-2xl mx-auto">
            <div className="bg-zinc-900 rounded-3xl border border-zinc-800 overflow-hidden">
              {/* Locked video preview */}
              <div className="aspect-video bg-gradient-to-br from-zinc-900 to-black relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-24 h-24 mx-auto mb-6 rounded-full bg-zinc-800 flex items-center justify-center"
                    >
                      <Lock className="w-12 h-12 text-zinc-500" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-white mb-2">This Event is Locked</h2>
                    <p className="text-zinc-400">Purchase a ticket to watch this event</p>
                  </div>
                </div>
                
                {/* Event badge */}
                <div className="absolute top-4 left-4">
                  {currentEvent.isLive ? (
                    <span className="flex items-center gap-2 px-3 py-1 bg-red-500/80 rounded-full">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      <span className="text-white font-bold text-sm">LIVE</span>
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-yellow-500/80 text-black font-bold text-sm rounded-full">
                      UPCOMING
                    </span>
                  )}
                </div>
              </div>

              {/* Unlock options */}
              <div className="p-8">
                <div className="text-center mb-8">
                  <h3 className="text-xl font-bold text-white mb-2">{currentEvent.title}</h3>
                  <p className="text-zinc-400">{currentEvent.subtitle}</p>
                  <div className="flex items-center justify-center gap-4 mt-4 text-sm text-zinc-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(currentEvent.date).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {currentEvent.time}
                    </span>
                  </div>
                </div>

                {/* Already purchased? Check access */}
                <div className="bg-zinc-800/50 rounded-xl p-6 mb-6">
                  <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-yellow-500" />
                    Already Purchased?
                  </h4>
                  <p className="text-sm text-zinc-400 mb-4">
                    Enter the email you used to purchase your ticket
                  </p>
                  <div className="flex gap-3">
                    <input
                      type="email"
                      value={accessEmail}
                      onChange={(e) => setAccessEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none"
                    />
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={checkAccessByEmail}
                      disabled={checkingAccess}
                      className="px-6 py-3 bg-yellow-500 text-black font-bold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50"
                    >
                      {checkingAccess ? 'Checking...' : 'Unlock'}
                    </motion.button>
                  </div>
                  {accessError && (
                    <p className="mt-3 text-sm text-red-400 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {accessError}
                    </p>
                  )}
                </div>

                {/* Buy ticket */}
                <div className="text-center">
                  <p className="text-zinc-500 text-sm mb-4">Don't have a ticket yet?</p>
                  <Link
                    to="/ppv"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-400 transition-all"
                  >
                    <Ticket className="w-5 h-5" />
                    Get Your Ticket
                    <ChevronRight className="w-5 h-5" />
                  </Link>
                  <p className="text-zinc-500 text-xs mt-4">
                    Starting at $9.99 • No account required
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
